import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogActions, DialogContent, DialogTitle, TextField, Grid,
  Select, MenuItem, FormControl, InputLabel, Checkbox, FormControlLabel,
  Button, IconButton, Typography, Box, Alert
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

// Re-using ANSWER_TYPES from AssessmentDefinitionManager or define locally if preferred
const ANSWER_TYPES = [
  'radio_likert_0_3', 'radio_likert_1_5', 'radio_custom', 
  'checkbox_custom', 'number_input', 'text_input_short', 'text_input_long'
];

const initialAnswerOptionState = { label: '', value: '' };

const QuestionEditorModal = ({ open, onClose, questionData, onSave, existingQuestionIds, questionIndex }) => {
  const [formData, setFormData] = useState(questionData);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    // Ensure answerOptions is always an array, even for new questions or types without options initially
    const initialQuestion = {
      questionId: '', text: '', answerType: 'radio_likert_0_3', 
      answerOptions: [initialAnswerOptionState], required: true, critical: false
    };
    const dataToEdit = questionData 
      ? { ...initialQuestion, ...questionData, answerOptions: questionData.answerOptions?.length ? questionData.answerOptions : [initialAnswerOptionState] }
      : initialQuestion;
    setFormData(dataToEdit);
    setFormErrors({}); // Clear errors when dialog opens or data changes
  }, [questionData, open]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAnswerOptionChange = (index, e) => {
    const { name, value } = e.target;
    const newOptions = [...formData.answerOptions];
    newOptions[index] = { ...newOptions[index], [name]: value };
    setFormData(prev => ({ ...prev, answerOptions: newOptions }));
  };

  const addAnswerOption = () => {
    setFormData(prev => ({
      ...prev,
      answerOptions: [...prev.answerOptions, initialAnswerOptionState]
    }));
  };

  const removeAnswerOption = (index) => {
    const newOptions = [...formData.answerOptions];
    newOptions.splice(index, 1);
    setFormData(prev => ({ ...prev, answerOptions: newOptions }));
  };

  const validateModalForm = () => {
    const errors = {};
    if (!formData.questionId.trim()) {
      errors.questionId = 'Question ID is required.';
    } else if (existingQuestionIds && existingQuestionIds.includes(formData.questionId) && (!questionData || questionData.questionId !== formData.questionId)) {
      // Check for uniqueness only if it's a new question or the ID has changed
      errors.questionId = 'Question ID must be unique within this test definition.';
    }
    if (!formData.text.trim()) errors.text = 'Question Text is required.';
    
    if ((formData.answerType === 'radio_custom' || formData.answerType === 'checkbox_custom')) {
      if (!formData.answerOptions || formData.answerOptions.length === 0) {
        errors.answerOptions = 'At least one answer option is required for custom types.';
      } else {
        formData.answerOptions.forEach((opt, i) => {
          if (!opt.label.trim()) errors[`option_label_${i}`] = `Label for option ${i+1} is required.`;
          if (opt.value === '' || opt.value === undefined || opt.value === null) errors[`option_value_${i}`] = `Value for option ${i+1} is required.`;
        });
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = () => {
    if (!validateModalForm()) return;
    onSave(formData, questionIndex); // Pass questionIndex if editing, null/undefined if new
    onClose();
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{questionData ? 'Edit Question' : 'Add New Question'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{mt:1}}>
          <Grid item xs={12} sm={6}>
            <TextField 
              fullWidth 
              label="Question ID" 
              name="questionId" 
              value={formData.questionId} 
              onChange={handleInputChange} 
              error={!!formErrors.questionId}
              helperText={formErrors.questionId}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!formErrors.answerType}>
              <InputLabel>Answer Type</InputLabel>
              <Select name="answerType" value={formData.answerType} label="Answer Type" onChange={handleInputChange}>
                {ANSWER_TYPES.map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField 
              fullWidth 
              label="Question Text" 
              name="text" 
              value={formData.text} 
              onChange={handleInputChange} 
              multiline 
              rows={3} 
              error={!!formErrors.text}
              helperText={formErrors.text}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel 
              control={<Checkbox checked={formData.required} onChange={handleInputChange} name="required" />} 
              label="Required" 
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel 
              control={<Checkbox checked={formData.critical} onChange={handleInputChange} name="critical" />} 
              label="Critical (Triggers Safety Alert)" 
            />
          </Grid>

          {(formData.answerType === 'radio_custom' || formData.answerType === 'checkbox_custom') && (
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{mt:1, mb:1}}>Answer Options</Typography>
              {formErrors.answerOptions && <Alert severity="error" size="small" sx={{mb:1}}>{formErrors.answerOptions}</Alert>}
              {formData.answerOptions.map((opt, optIndex) => (
                <Grid container spacing={1} key={optIndex} alignItems="center" sx={{mb:1.5}}>
                  <Grid item xs={12} sm={5}>
                    <TextField 
                      size="small" 
                      fullWidth 
                      label={`Option ${optIndex + 1} Label`}
                      name="label" 
                      value={opt.label} 
                      onChange={(e) => handleAnswerOptionChange(optIndex, e)}
                      error={!!formErrors[`option_label_${optIndex}`]}
                      helperText={formErrors[`option_label_${optIndex}`]}
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField 
                      size="small" 
                      fullWidth 
                      label={`Option ${optIndex + 1} Value`}
                      name="value" 
                      value={opt.value} 
                      onChange={(e) => handleAnswerOptionChange(optIndex, e)} 
                      error={!!formErrors[`option_value_${optIndex}`]}
                      helperText={formErrors[`option_value_${optIndex}`]}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <IconButton onClick={() => removeAnswerOption(optIndex)} disabled={formData.answerOptions.length <= 1}>
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
              <Button size="small" startIcon={<AddIcon />} onClick={addAnswerOption}>
                Add Answer Option
              </Button>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">Save Question</Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuestionEditorModal;
